import { HttpClient } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { ClientEnvironmentConfig } from '@core/config-models';
import { LocationService } from '@core/jss-seo';
import type { InstallationAddress } from '@essent/address';
import type { Gender } from '@essent/common';
import {
  postCreateLeadThirdParty,
  postCreateLeadThirdPartySuccess,
  type ProductTypeThirdParty,
} from '@essent/feh';
import type {
  ConfirmationConsent,
  FlowId,
  PutConfirmationPayload,
} from '@essent/new-customer';
import {
  AdditionalInformationType,
  ConsentStatus,
  IncomingPaymentMethod,
  PreferredChannel,
  putAdditionalInformation,
  putAdditionalInformationSuccess,
  putBudgetBill,
  putBudgetBillSuccess,
  putConfirmation,
  putConfirmationSuccess,
  putCorrespondenceDetails,
  putCorrespondenceDetailsSuccess,
  putOrganisationDetails,
  putPaymentDetails,
  putPaymentDetailsSuccess,
  putPersonalDetails,
  putStartDate,
} from '@essent/new-customer';
import { getMGMProductCode } from '@innogy/become-a-customer/shared';
import { MarkAsyncTasksAsPendingAction } from '@innogy/progressive-ngrx-forms';
import {
  getSaleId,
  hasMemberGetMember,
  memberGetMemberSaleSubmit,
  setMGMCustomerDetails,
  type MGMBuyerInfo,
} from '@innogy/shared/member-get-member';
import {
  isValidatedSubmitAction,
  normalizeDateString,
} from '@innogy/utils-deprecated';
import { ofActionId } from '@innogy/utils-rxjs';
import { CustomerType } from '@integration/base-models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { addHours, addWeeks, format, max, startOfDay } from 'date-fns';
import { electronicFormatIBAN } from 'ibantools';
import {
  ClearAsyncErrorAction,
  StartAsyncValidationAction,
  type FormControlState,
} from 'ngrx-forms';
import { combineLatest } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import {
  setContractWithOtherSupplierAction,
  setResidentialAction,
} from '../bac/15.additional-information/additional-information.actions';
import { getSupplyAddressState } from '../bac/2.supply-address';
import { getConsumptionValuesVM } from '../bac/7.consumption';
import { getFlowId, noFlowIdErrorAction } from '../bac/flow-id';
import { getIsCustomer } from '../bac/is-customer';
import { getPropositionOffer } from '../bac/offers';
import { getPartnerId } from '../bac/partner-id';
import { getFunnelSettings } from '../funnel';
import { getOffer } from '../get-offer/get-offer.actions';
import {
  getOrderAddressFormState,
  getSelectedCorrespondenceInstallationAddressState,
  ORDER_ADDRESS_FORM_ID,
} from './address';
import {
  getOrderConfirmationFormState,
  ORDER_CONFIRMATION_FORM_ID,
} from './confirmation';
import {
  getOrderCrossSellFormState,
  getOrderCrossSellProduct,
  ORDER_CROSS_SELL_FORM_ID,
} from './cross-sell';
import { getOrderCustomerTypeFormState } from './customer-type';
import {
  confirmOrderWithoutCrossSellLead,
  ORDER_REFRESH_OFFER_ACTION_ID,
  redirectToSuccessPageAction,
  resetAtoConsentAction,
  resetOrderConditionsAction,
  SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID,
} from './order.actions';
import { getOrderState } from './order.selector';
import type { OrderState } from './order.state';
import { getOrderPaymentFormState, ORDER_PAYMENT_FORM_ID } from './payment';
import { getOrderPersonalFormState, ORDER_PERSONAL_FORM_ID } from './personal';

@Injectable()
export class OrderFormSubmitEffects {
  private readonly http = inject(HttpClient);

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<any>,
    @Inject(ENVIRONMENT_CONFIG)
    private readonly environmentConfig: ClientEnvironmentConfig,
    private readonly location: LocationService
  ) {}

  private readonly funnelSettings$ = this.store$.select(getFunnelSettings);
  private readonly flowId$ = this.store$.pipe(select(getFlowId));
  private readonly partnerId$ = this.store$.pipe(select(getPartnerId));
  private readonly mgmSaleId$ = this.store$.pipe(select(getSaleId));
  private readonly hasMemberGetMember$ = this.store$.pipe(
    select(hasMemberGetMember)
  );
  private readonly memberGetMemberSaleId$ = this.store$.pipe(select(getSaleId));
  private readonly selectedOffer$ = this.store$.pipe(
    select(getPropositionOffer)
  );
  private readonly personalDetailsFormValues$ = this.store$.pipe(
    select(getOrderPersonalFormState)
  );
  private readonly addressDetailsFormValues$ = this.store$.pipe(
    select(getOrderAddressFormState)
  );
  private readonly crossSellDetailsFormValues$ = this.store$.pipe(
    select(getOrderCrossSellFormState)
  );
  private readonly customerTypeDetailsFormValues$ = this.store$.pipe(
    select(getOrderCustomerTypeFormState)
  );
  private readonly crossSellProduct$ = this.store$.pipe(
    select(getOrderCrossSellProduct)
  );
  private readonly paymentDetailsFormValues$ = this.store$.pipe(
    select(getOrderPaymentFormState)
  );
  private readonly confirmationFormValues$ = this.store$.pipe(
    select(getOrderConfirmationFormState)
  );
  private readonly selectedCorrespondenceInstallationAddress$ =
    this.store$.pipe(select(getSelectedCorrespondenceInstallationAddressState));

  consumptionValuesVM$ = this.store$.pipe(select(getConsumptionValuesVM));
  getSupplyAddressState$ = this.store$.pipe(select(getSupplyAddressState));
  propositionOffer$ = this.store$.pipe(select(getPropositionOffer));
  isCustomer$ = this.store$.pipe(select(getIsCustomer));

  private readonly memberGetMemberPayload$ = combineLatest([
    this.personalDetailsFormValues$,
    this.paymentDetailsFormValues$,
    this.memberGetMemberSaleId$,
    this.selectedOffer$,
  ]).pipe(
    map(
      ([
        personalDetailsFormValues,
        paymentDetailsFormValues,
        mgmSaleId,
        selectedOffer,
      ]): MGMBuyerInfo | null => {
        if (mgmSaleId && selectedOffer) {
          return {
            name: `${personalDetailsFormValues.value.initials} ${personalDetailsFormValues.value.lastName}`,
            email: personalDetailsFormValues.value.email,
            bank_account: paymentDetailsFormValues.value.iban,
            sale_id: mgmSaleId,
            product: getMGMProductCode(
              selectedOffer,
              this.environmentConfig.brand
            ),
            state: 'confirmed' as const,
          };
        }
        return null;
      }
    )
  );

  private readonly RESET_CHECKS_LIST = [
    ORDER_PERSONAL_FORM_ID,
    ORDER_ADDRESS_FORM_ID,
    ORDER_CROSS_SELL_FORM_ID,
    ORDER_CONFIRMATION_FORM_ID,
    ORDER_PAYMENT_FORM_ID,
  ];

  private readonly SEGMENT = this.environmentConfig.segment;

  // In order to efficiently test this we need to use the mockStore in this file's spec.
  // This is currently technical debt, hence this effect is ignored in the coverage report.
  /* istanbul ignore next */
  public resetConditionsAndConsent$ = createEffect(() =>
    this.actions$.pipe(
      isValidatedSubmitAction(
        getOrderState,
        this.RESET_CHECKS_LIST,
        'formState'
      )(this.store$),
      mergeMap(() => [resetOrderConditionsAction(), resetAtoConsentAction()])
    )
  );

  /**
   * Step 2
   * Personal details card - Essent/Energiedirect Consumer
   */
  public readonly submitPersonalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) => action.stepId === ORDER_PERSONAL_FORM_ID),
      filter(() => this.SEGMENT !== 'zakelijk'),
      concatLatestFrom(() => [this.flowId$, this.personalDetailsFormValues$]),
      switchMap(([, flowId, personalDetailsForm]) => {
        if (flowId) {
          return [
            putPersonalDetails({
              payload: {
                metaData: {
                  flowId,
                },
                payload: {
                  prefix: personalDetailsForm.value?.prefix || undefined,
                  initials: personalDetailsForm.value.initials,
                  lastName: personalDetailsForm.value.lastName,
                  dateOfBirth: normalizeDateString(
                    formatDate(personalDetailsForm.value.dateOfBirth)
                  ),
                  gender: personalDetailsForm.value.gender as Gender,
                },
              },
            }),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );

  /**
   * Step 2.1
   * Submit company details (SME only)
   */
  public readonly submitOrganisationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) => action.stepId === ORDER_PERSONAL_FORM_ID),
      filter(() => this.SEGMENT === 'zakelijk'),
      concatLatestFrom(() => [this.flowId$, this.personalDetailsFormValues$]),
      switchMap(([_, flowId, personalDetailsForm]) => {
        if (flowId) {
          return [
            putOrganisationDetails({
              payload: {
                metaData: {
                  flowId,
                },
                payload: {
                  organisationName: personalDetailsForm.value.companyName,
                  chamberOfCommerceNumber: personalDetailsForm.value.kvkNumber,
                },
              },
            }),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );

  /**
   * Step 3.1
   * Submit correspondence details. This effect also saves the phone number provided
   * by the ORDER_PERSONAL_FORM.
   */
  public readonly submitCorrespondenceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) =>
        [ORDER_ADDRESS_FORM_ID, ORDER_PERSONAL_FORM_ID].includes(
          action.stepId as keyof OrderState
        )
      ),
      concatLatestFrom(() => [
        this.flowId$,
        this.personalDetailsFormValues$,
        this.addressDetailsFormValues$,
        this.selectedCorrespondenceInstallationAddress$,
      ]),
      // If the address-details-form is not touched yet the effect is triggered by the
      // ORDER_PERSONAL_FORM. There is no need to save the default form values.
      filter(([, , , addressdetailsForm]) => addressdetailsForm.isTouched),
      switchMap(
        ([
          _,
          flowId,
          personalDetailsForm,
          addressDetailsForm,
          selectedCorrespondenceInstallationAddress,
        ]) => {
          if (flowId) {
            const { useAsCorrespondenceAddress } = addressDetailsForm.value;

            const useSupplyAddress = this.shouldUseAsCorrespondenceAddress(
              useAsCorrespondenceAddress
            );
            const correspondenceAddress = this.formatCorrespondenceAddress(
              useSupplyAddress,
              selectedCorrespondenceInstallationAddress,
              addressDetailsForm.value.careOfFullName
            );

            return [
              putCorrespondenceDetails({
                actionId: SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID,
                payload: {
                  metaData: {
                    flowId,
                  },
                  payload: {
                    phone: personalDetailsForm.value.phone,
                    email: personalDetailsForm.value.email,
                    preferredChannel: PreferredChannel.EMAIL,
                    useSupplyAddress,
                    correspondenceAddress,
                  },
                },
              }),
            ];
          }
          return [noFlowIdErrorAction()];
        }
      )
    )
  );

  /**
   * Step 4.1
   * Address details card
   * Submit additional information
   */
  public readonly submitAdditionalInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putCorrespondenceDetailsSuccess),
      ofActionId(SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID),
      concatLatestFrom(() => [this.flowId$, this.addressDetailsFormValues$]),
      switchMap(([_, flowId, addressDetailsFormValues]) => {
        const { residenceOrWorkplace, inswitchInhouse } =
          addressDetailsFormValues.value;

        if (flowId) {
          const inswitchInhouseValue = inswitchInhouse?.value?.type;
          const isInswitch = inswitchInhouseValue === 'inswitch';

          return [
            setResidentialAction({ value: residenceOrWorkplace }),
            setContractWithOtherSupplierAction({
              value: isInswitch,
            }),
            putAdditionalInformation({
              actionId: SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID,
              payload: {
                metaData: {
                  flowId,
                },
                payload: [
                  {
                    name: AdditionalInformationType.RESIDENTIAL,
                    value: residenceOrWorkplace,
                  },
                  {
                    name: AdditionalInformationType.CONTRACT_WITH_ANOTHER_SUPPLIER,
                    value: isInswitch,
                  },
                ],
              },
            }),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );

  /**
   * Step 4.2
   * Address details card
   * Submit start date
   */
  public readonly submitStartDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putAdditionalInformationSuccess),
      ofActionId(SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID),
      concatLatestFrom(() => [
        this.flowId$,
        this.addressDetailsFormValues$,
        this.selectedOffer$,
      ]),
      switchMap(([_, flowId, addressDetailsFormValues, activeOffer]) => {
        const { selectedStartDate } = addressDetailsFormValues.value;

        if (!flowId) {
          return [noFlowIdErrorAction()];
        }

        if (activeOffer) {
          const commodityStartDatePayload = activeOffer.commodities.map(
            (commodity) => ({
              energyType: commodity,
              startDate: selectedStartDate,
            })
          );

          return [
            putStartDate({
              actionId: SUBMIT_CORRESPONDENCE_DETAILS_ACTION_ID,
              payload: {
                metaData: {
                  flowId,
                },
                payload: commodityStartDatePayload,
              },
            }),
          ];
        }

        return [];
      })
    )
  );

  /**
   * Step 4.3
   * Address details card
   * Refresh offers when additional information has been changed successfully
   */
  public readonly refreshOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putAdditionalInformationSuccess),
      filter((action) => action.actionId === ORDER_REFRESH_OFFER_ACTION_ID),
      concatLatestFrom(() => [
        this.flowId$,
        this.propositionOffer$,
        this.isCustomer$,
      ]),
      switchMap(([_, flowId, propositionOffer, isCustomer]) => {
        if (flowId && propositionOffer) {
          return [
            getOffer({
              actionId: ORDER_REFRESH_OFFER_ACTION_ID,
              payload: {
                flowId: flowId as unknown as FlowId,
                campaignId: propositionOffer.campaignId,
                duration: propositionOffer.duration,
                incentiveId: propositionOffer.incentiveId,
                isCustomer,
              },
            }),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );

  /**
   * Step 5
   * Payment details card
   */
  public readonly submitPaymentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) => action.stepId === ORDER_PAYMENT_FORM_ID),
      concatLatestFrom(() => [this.flowId$, this.paymentDetailsFormValues$]),
      filter(
        ([_, __, paymentDetailsFormValues]) =>
          paymentDetailsFormValues.controls.iban.isValid
      ),
      switchMap(([_, flowId, paymentDetailsFormValues]) => {
        if (flowId) {
          return [
            putPaymentDetails({
              payload: {
                metaData: {
                  flowId,
                },
                payload: {
                  iban:
                    electronicFormatIBAN(paymentDetailsFormValues.value.iban) ??
                    paymentDetailsFormValues.value.iban,
                  incomingPaymentMethod: IncomingPaymentMethod.DIRECT_DEBIT,
                  useCorrespondenceAddress: true,
                  paymentTerm: this.SEGMENT === 'zakelijk' ? 14 : undefined,
                },
              },
            }),
            new StartAsyncValidationAction(
              paymentDetailsFormValues.controls.iban.id,
              'ibanBlacklisted'
            ),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );
  public markAsValidPaymentDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putPaymentDetailsSuccess),
      concatLatestFrom(() => [this.paymentDetailsFormValues$]),
      mergeMap(([_, paymentDetailsFormValues]) => {
        return [
          new ClearAsyncErrorAction(
            paymentDetailsFormValues.controls.iban.id,
            'ibanBlacklisted'
          ),
        ];
      })
    )
  );

  /**
   * Step 6
   * Confirmation card
   */
  public readonly submitBudgetBill$ = createEffect(() =>
    this.actions$.pipe(
      ofType<MarkAsyncTasksAsPendingAction>(MarkAsyncTasksAsPendingAction.TYPE),
      filter((action) => action.stepId === ORDER_CONFIRMATION_FORM_ID),
      concatLatestFrom(() => [this.flowId$, this.selectedOffer$]),
      switchMap(([_, flowId, activeOffer]) => {
        if (flowId && activeOffer) {
          return [
            putBudgetBill({
              payload: {
                metaData: { flowId },
                payload: {
                  budgetBillAmount: activeOffer.budgetBillAmount ?? NaN,
                },
              },
            }),
          ];
        }
        return [noFlowIdErrorAction()];
      })
    )
  );

  public readonly submitCrossSellProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putBudgetBillSuccess),
      concatLatestFrom(() => [
        this.crossSellDetailsFormValues$,
        this.crossSellProduct$,
        this.personalDetailsFormValues$,
        this.getSupplyAddressState$,
        this.paymentDetailsFormValues$,
      ]),
      switchMap(
        ([
          _,
          crossSellDetails,
          crossSellProduct,
          personalDetails,
          supplyAddress,
          paymentDetails,
        ]) => {
          if (!crossSellDetails.value.hasSelectedProduct) {
            return [confirmOrderWithoutCrossSellLead()];
          }

          return [
            postCreateLeadThirdParty({
              payload: {
                lead: {
                  title: this.formatTitle(
                    personalDetails.value.gender as Gender
                  ),
                  initials: personalDetails.value.initials,
                  firstName: personalDetails.value.initials,
                  ...(personalDetails.value.prefix
                    ? { prefix: personalDetails.value.prefix }
                    : {}),
                  lastName: personalDetails.value.lastName,
                  phone: personalDetails.value.phone,
                  email: personalDetails.value.email,
                  deliveryAddress: {
                    postcode: supplyAddress?.data?.postcode ?? '',
                    houseNumber: supplyAddress?.data?.houseNumber ?? '',
                    houseNumberExtension:
                      supplyAddress?.data?.houseNumberExtension,
                    street: supplyAddress?.data?.street ?? '',
                    city: supplyAddress?.data?.city ?? '',
                  },
                },
                requestContext: {
                  channel: 'Online',
                  subChannel: 'Acquisitiefunnel',
                  requestType: 'Directe Offerte Aanvraag',
                  brand: 'essent',
                  productType:
                    crossSellProduct?.suiteCrmId as ProductTypeThirdParty,
                  campaignId: crossSellProduct?.campaignId ?? '',
                  campaignName: crossSellProduct?.campaignId ?? '',
                  servicePartnerBranch: '',
                },
                offeredProduct: {
                  category: crossSellProduct?.suiteCrmId ?? '',
                  description: `${crossSellProduct?.content.title} - ${crossSellProduct?.content.subTitle}`,
                },
                temperature: 4,
                createdOn: new Date().toISOString(),
                remarks: [
                  'Merk onbekend',
                  'leeftijd onbekend',
                  'ander apparaat voor verwarming onbekend',
                ].join(','),
                iban: paymentDetails.value.iban.replace(/\s/g, ''),
              },
            }),
          ];
        }
      )
    )
  );

  public readonly submitConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(postCreateLeadThirdPartySuccess, confirmOrderWithoutCrossSellLead),
      concatLatestFrom(() => [
        this.flowId$,
        this.partnerId$,
        this.mgmSaleId$,
        this.confirmationFormValues$,
        this.customerTypeDetailsFormValues$,
      ]),
      switchMap(
        ([
          _,
          flowId,
          extPartnerId,
          mgmSaleId,
          formValues,
          customerTypeValues,
        ]) => {
          if (!flowId) {
            return [noFlowIdErrorAction()];
          }

          const today = new Date();
          const validFrom = normalizeDateString(
            formatDate(
              `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`
            )
          );
          const mapConsentApproval = (
            control: FormControlState<boolean>
          ): ConfirmationConsent => ({
            validFrom,
            consentStatus:
              control.value === true
                ? ConsentStatus.GIVEN
                : ConsentStatus.REJECTED,
          });
          const mapConsentRejection = (
            control: FormControlState<boolean>
          ): ConfirmationConsent => ({
            validFrom,
            consentStatus:
              control.value === true
                ? ConsentStatus.REJECTED
                : ConsentStatus.GIVEN,
          });

          const consents = {
            sustainabilityAdvicePhone: mapConsentApproval(
              formValues.controls.sustainabilityAdvicePhoneAccepted
            ),
            energyAdvicePhone: mapConsentApproval(
              formValues.controls.energyAdvicePhoneAccepted
            ),
            sustainabilityAdviceEmail: mapConsentRejection(
              formValues.controls.sustainabilityAdviceEmailRejected
            ),
            energyAdviceEmail: mapConsentRejection(
              formValues.controls.energyAdviceEmailRejected
            ),
          };

          let customerType: CustomerType | undefined = undefined;
          if (customerTypeValues.value.segmentQuestion) {
            if (
              customerTypeValues.value.segmentQuestion !==
              CustomerType.HOUSEHOLD
            ) {
              customerType = customerTypeValues.value.companySize;
            } else {
              customerType = customerTypeValues.value.segmentQuestion;
            }
          }

          const putConfirmationPayload: PutConfirmationPayload = {
            metaData: { flowId },
            payload: {
              sendQuotation: false,
              extPartnerId,
              consents,
              customerType,
              atoConsented: formValues.controls.atoConsented.value,
            },
          };

          if (mgmSaleId && putConfirmationPayload.payload) {
            putConfirmationPayload.payload.promoter = mgmSaleId;
          }

          return [
            putConfirmation({
              payload: putConfirmationPayload,
            }),
          ];
        }
      )
    )
  );

  /**
   * Step 6b
   * Finish the order and check for MemberGetMember or redirect to thank you page
   */
  public readonly finishOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putConfirmationSuccess),
      concatLatestFrom(() => [
        this.hasMemberGetMember$,
        this.memberGetMemberPayload$,
      ]),
      switchMap(([_, hasMGM, memberGetMemberPayload]) => {
        if (hasMGM && memberGetMemberPayload) {
          return [
            memberGetMemberSaleSubmit({ payload: memberGetMemberPayload }),
            redirectToSuccessPageAction(),
          ];
        }
        return [redirectToSuccessPageAction()];
      })
    )
  );

  public readonly sendLoyaltyLead$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(putConfirmationSuccess),
        concatLatestFrom(() => [
          this.crossSellDetailsFormValues$,
          this.personalDetailsFormValues$,
          this.addressDetailsFormValues$,
        ]),
        filter(
          ([
            ,
            {
              value: { hasSelectedLoyaltyProgram },
            },
          ]) => !!hasSelectedLoyaltyProgram
        ),
        switchMap(
          ([_, , personalDetailsFormValues, addressDetailsFormValues]) => {
            // The email will be send out a week after the contract has started, taking into account the cooldown period
            const emailDate = leadEmailDate(
              addressDetailsFormValues.value.selectedStartDate
            );

            // In scope for refactor with https://jira.essent.nl/jira/browse/CTR-2190
            return this.http.post('/api/thuisvoordeel-interest-trigger', {
              parameter_values: {
                customerId: '',
                email: personalDetailsFormValues.value.email,
                gender: personalDetailsFormValues.value.gender,
                initials: personalDetailsFormValues.value.initials,
                prefix: personalDetailsFormValues.value.prefix ?? '',
                lastName: personalDetailsFormValues.value.lastName,
                MAILNAME_FULL:
                  'rc_20251103_ess_loy_0272_acq_thuisvoordeel_werven',
                UITING: 'ess_loy_0272',
                INSTANT: 'false',
                SENDOUT_DT: format(emailDate, 'yyyy-MM-dd HH:mm'),
                DESCRIPTION_1: 'Startdatum contract',
                VALUE_1: format(
                  new Date(addressDetailsFormValues.value.selectedStartDate),
                  'yyyy-MM-dd'
                ),
                DESCRIPTION_2: '',
                VALUE_2: '',
                DESCRIPTION_3: '',
                VALUE_3: '',
                DESCRIPTION_4: '',
                VALUE_4: '',
                DESCRIPTION_5: '',
                VALUE_5: '',
                DESCRIPTION_6: '',
                VALUE_6: '',
              },
            });
          }
        )
      ),
    { dispatch: false }
  );

  public readonly prefillMemberGetMemberWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(putConfirmationSuccess),
      withLatestFrom(this.personalDetailsFormValues$),
      map(([_, personalDetailsFormValues]) =>
        setMGMCustomerDetails({
          customerName: `${personalDetailsFormValues.value.initials} ${personalDetailsFormValues.value.lastName}`,
          customerEmail: personalDetailsFormValues.value.email,
        })
      )
    )
  );

  /**
   * Step 6d
   * Finish the order and redirect to thank you page
   */
  public readonly redirectToSuccessPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(redirectToSuccessPageAction),
        concatLatestFrom(() => this.funnelSettings$),
        tap(([_, settings]) => {
          const redirectUrl = settings.orderSuccesPage;

          return this.location.navigateScLink(redirectUrl, '/');
        })
      ),
    { dispatch: false }
  );

  private shouldUseAsCorrespondenceAddress(
    _useCorrespondenceAddress: boolean | undefined
  ) {
    return _useCorrespondenceAddress !== undefined
      ? _useCorrespondenceAddress
      : true;
  }

  private formatCorrespondenceAddress(
    _useSupplyAddress: boolean,
    address: Partial<InstallationAddress> | undefined,
    careOfFullName?: string
  ) {
    if (_useSupplyAddress) {
      return undefined;
    }
    return {
      city: address?.city || '',
      houseNumber: address?.houseNumber || '',
      postcode: address?.postcode || '',
      street: address?.street || '',
      houseNumberExtension: address?.houseNumberExtension,
      careOfFullName,
    };
  }

  private formatTitle(gender: Gender): string {
    switch (gender) {
      case 'male':
        return 'Heer';
      case 'female':
        return 'Mevrouw';
      case 'other':
      default:
        return 'Persoon';
    }
  }
}

export function leadEmailDate(selectedStartDate: string): Date {
  // Take the date after cooldown or selected start date if further in the future
  const startDateAdjustedForCooldown = max([
    startOfDay(new Date(selectedStartDate)),
    startOfDay(addWeeks(new Date(), 2)),
  ]);
  // Add a week and 11 hours for the email to send out
  return addHours(addWeeks(startDateAdjustedForCooldown, 1), 11);
}

export function formatDate(date: string): string {
  return date
    .split('-')
    .map((item) => {
      return item.replace(/^[1-9]$/g, '0$&');
    })
    .join('-');
}
